import { useState } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import {
  setWalkThroughStatus as setWalkThroughStatusAPI,
  deleteWalkthrough as deleteWalkthroughAPI,
} from 'src/services/client';

// TODO: phase 3 - this hook calls the API directly (the modern path). The
// legacy `setWalkThroughStatus` and `onDeleteWalkthrough` Redux thunks still
// exist with 7+ other callers (HomeOwnerDashboard, PropertySuites,
// ConfigureSubArea, ViewAllAreasAndFloorPlan, ViewSubAreas,
// DeveloperWalkthrough). Migrate each caller to this hook (or a shared
// mutation hook) and delete the thunks from src/store/actions/suites.ts and
// src/store/actions/walkThroughs.ts.
// Asana (Retire legacy BOS setWalkThroughStatus Redux thunk): https://app.asana.com/1/41213529744312/project/1208712340140532/task/1214631846805256?focus=true
// Asana (Retire legacy BOS onDeleteWalkthrough Redux thunk): https://app.asana.com/1/41213529744312/project/1208712340140532/task/1214631846805260?focus=true

const TOAST_OPTIONS = {
  autoClose: 3000,
  position: 'top-center' as const,
};

const REVIEW_SUCCESS_MESSAGE =
  'ⓘ  Congratulations! You have successfully completed the walk-through, We will send an email notification once the PDF is ready for your review.';

export interface UseWalkthroughMutationsArgs {
  refetchWalkthroughs: () => Promise<void>;
  refreshSuiteCounts: () => Promise<void>;
}

export interface UseWalkthroughMutationsReturn {
  setWalkthroughStatus: (status: any) => Promise<boolean>;
  deleteWalkthrough: (walkthroughId: string, userId: string) => Promise<void>;
  isMutating: boolean;
}

export const useWalkthroughMutations = ({
  refetchWalkthroughs,
  refreshSuiteCounts,
}: UseWalkthroughMutationsArgs): UseWalkthroughMutationsReturn => {
  const [isMutating, setIsMutating] = useState<boolean>(false);

  const setWalkthroughStatus = async (status: any): Promise<boolean> => {
    setIsMutating(true);

    const augmentedStatus = {
      ...status,
      ReviewDate: moment().format('MM-DD-YYYY HH:mm:ss'),
    };

    try {
      const response: any = await setWalkThroughStatusAPI(augmentedStatus);

      if (!(response.ok || response.status === 200)) {
        toast.error(
          ' 🚫 Something went wrong, please try again.',
          TOAST_OPTIONS
        );
        return false;
      }

      const responseJson = await response.json();

      if (responseJson.statusCode !== 200) {
        toast.error(
          ' 🚫 Walkthrough status updating failed, please try again.',
          TOAST_OPTIONS
        );
        return false;
      }

      if (augmentedStatus.Status === 'Review') {
        toast.success(REVIEW_SUCCESS_MESSAGE, TOAST_OPTIONS);
        await refreshSuiteCounts();
      }

      await refetchWalkthroughs();
      return true;
    } catch (error) {
      toast.error(
        ' 🚫 Something went wrong, please try again.',
        TOAST_OPTIONS
      );
      return false;
    } finally {
      setIsMutating(false);
    }
  };

  const deleteWalkthrough = async (
    walkthroughId: string,
    userId: string
  ): Promise<void> => {
    setIsMutating(true);

    try {
      const response: any = await deleteWalkthroughAPI(walkthroughId, userId);

      if (response.status !== 200) {
        toast.error('Walkthrough deleting failed.', TOAST_OPTIONS);
        return;
      }

      const responseJson = await response.json();

      if (responseJson.statusCode !== 200) {
        toast.error('Walkthrough deleting failed.', TOAST_OPTIONS);
        return;
      }

      toast.success('Walkthrough deleted successfully.', TOAST_OPTIONS);

      await refetchWalkthroughs();
      await refreshSuiteCounts();
    } catch (error) {
      toast.error(
        ' 🚫 Something went wrong while deleting walkthrough, please try again.',
        TOAST_OPTIONS
      );
    } finally {
      setIsMutating(false);
    }
  };

  return {
    setWalkthroughStatus,
    deleteWalkthrough,
    isMutating,
  };
};
